/* Trust page copy (Arabic / العربية) — Modern Standard Arabic.
 *
 * Style rules: docs/arabic-style-guide.md (locked 2026-07-12). Meaning is kept
 * 1:1 with the approved English in trust-en.ts; syntax is restructured into
 * natural Arabic, not calqued. No em/en dashes. Brand: برَينوتس (fatha on the
 * ر, locked 2026-07-14). Latin trademarks stay Latin (Oracle, OpenAI, SDAIA
 * renders as سدايا per the legal pages).
 *
 * THE PILLAR CONCEIT DOES NOT TRANSLATE. English builds the page on the "Pri"
 * acrostic (Principles · Privacy · Prime · Priority) with the shared prefix in
 * teal. The Arabic hero words are translations that share no prefix
 * (مبادئ / خصوصية / امتياز / أولوية, locked in hero-pri-words.ts), so the AR
 * pillars carry NO teal prefix and the section title leans on the brand itself
 * instead of an acrostic. Do not invent Arabic words that start alike just to
 * recreate the English game; that would trade meaning for a pun the reader
 * cannot see.
 *
 * Every claim is cross-checked against the Arabic Privacy/Terms/Sub-processors
 * so the pages never contradict each other, same list as trust-en.ts:
 *  - Data STORED in the Kingdom (Riyadh live, Jeddah backups), but content IS
 *    sent to AI providers abroad to transcribe/summarize -> stated openly.
 *  - Backups: encrypted, keys held apart, purged within 30 days. The 21-day
 *    immutability is added detail, not a conflict.
 *  - AI-training claim scoped to "our AI providers".
 *  - Audio is deleted from OUR systems as soon as processing finishes;
 *    retention is opt-in and paid-plan only; a connected Drive keeps the
 *    user's own copy, which our deletion cannot reach.
 */

import type { TrustDoc } from './trust-en';

export const TRUST_AR: TrustDoc = {
  eyebrow: 'الثقة والأمان',
  title: 'بياناتك ملك لك.',
  lead: 'اجتماعاتك ومحادثاتك وبياناتك تبقى لك وحدك. برَينوتس مسجل في السجل الوطني لحماية البيانات الشخصية في المملكة، ويخزن بياناتك داخل المملكة، ولا يستخدمها أبدًا لتدريب نماذج الذكاء الاصطناعي.',

  a11y: { reg: 'التسجيل لدى سدايا', badges: 'الشهادات والامتثال' },
  brand: { pri: 'برَي', rest: 'نوتس' },

  reg: {
    heading: 'مسجل في السجل الوطني لحماية البيانات الشخصية',
    regLabel: 'رقم التسجيل',
    regNo: '3260007171',
    statusLabel: 'الحالة',
    status: 'نشط',
    issuedLabel: 'تاريخ الإصدار',
    issued: '18 يوليو 2026',
    platform:
      'عبر منصة حوكمة البيانات الوطنية (NDGP)، التي تشغلها الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا).',
    verifyUrl:
      'https://dgp.sdaia.gov.sa/wps/portal/pdp/services/certificate/ee68dcfb-d682-f111-b128-005056ab563b/!ut/p/z1/jY_RCoIwFIafxQcYO9vYWJcWQlqLQjTbTWzpTCiVkC56-kaXSdq5--H7f86HNS6wbs2zqc3QdK25-XzS4hyHkViTFCjJlxwEUzvON4xCxPDxC8jyhQcOVCVhzGDPsP6nDz8uhLl-Mgd4A_pQK1Vj3ZvhiprWdbioKiHLi7OoFJIiRwhBllCJADhwYSwXzPrf9OS6pCNgrP8BJvz6e1a8ti6N6yB4A6PjMao!/',
    verifyLabel: 'تحقق عبر سدايا',
    qrCaption: 'امسح رمز QR للتحقق',
    logoAlt: {
      sdaia: 'الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا)',
      ndgp: 'منصة حوكمة البيانات الوطنية',
    },
  },

  badges: [
    {
      icon: 'shield-check',
      title: 'مسجل لدى سدايا',
      line: 'مسجل في السجل الوطني لحماية البيانات الشخصية بالمملكة، برقم 3260007171.',
    },
    {
      icon: 'pin',
      title: 'مستضاف داخل المملكة',
      line: 'تُخزن بياناتك وتُنسخ احتياطيًا داخل المملكة العربية السعودية.',
    },
    {
      icon: 'doc',
      title: 'متوافق مع نظام حماية البيانات الشخصية',
      line: 'مبني وفق نظام حماية البيانات الشخصية السعودي ولائحته التنفيذية.',
    },
    {
      icon: 'lock',
      title: 'بنية تحتية بمستوى المؤسسات',
      line: 'يعمل على Oracle Cloud Infrastructure، الحاصلة على شهادتي ISO 27001 وSOC 2 من جهات مستقلة.',
    },
  ],

  pillarsTitle: 'ركائز الثقة في برَينوتس',
  pillars: [
    {
      id: 'principles',
      icon: 'shield-check',
      word: 'مبادئ',
      subtitle: 'بياناتك ملك لك.',
      summary:
        'بنينا برَينوتس على قناعة واحدة: محادثاتك واجتماعاتك وبياناتك ملك لك، ليس لنا. نحتفظ ببياناتك فقط لتشغيل الخدمة، ولا نستخدمها لأي غرض آخر. وكل ما نفعله بها واضح ويمكنك التحقق منه بنفسك.',
      detailLabel: 'ما نلتزم به',
      groups: [
        {
          points: [
            {
              label: 'اجتماعاتك ملك لك.',
              body: 'محادثاتك واجتماعاتك، بتسجيلاتها ومحاضرها وملاحظاتها، ملك لك. نحن حافظون عليها لا مالكون لها، وأنت من يقرر مصيرها.',
            },
            {
              label: 'مسؤولون، لا مجرد مدّعين.',
              body: 'تشغّل برَينوتس شركة برييورا، المسجلة في السجل الوطني لحماية البيانات الشخصية لدى سدايا (رقم 3260007171، نشط)، والمبنية وفق نظام حماية البيانات الشخصية السعودي. امسح الرمز أعلاه للتحقق بنفسك.',
            },
            {
              label: 'الشفافية أصل لا استثناء.',
              body: 'نفصح عن كل جهة تساعدنا في تشغيل برَينوتس، ودورها، وإلى أين تذهب بياناتك. ولا نخفي شيئا عن طريقة تعاملنا معها.',
            },
          ],
        },
      ],
      linkTo: '/privacy',
      linkLabel: 'اقرأ سياسة الخصوصية',
    },
    {
      id: 'privacy',
      icon: 'no-ai',
      word: 'خصوصية',
      subtitle: 'لا تُباع، ولا تُدرَّب عليها نماذج الذكاء الاصطناعي.',
      summary:
        'لا نبيع بياناتك أبدًا، ولا نستخدمها للإعلانات، ولا ندرب عليها نماذج الذكاء الاصطناعي. وتُقرأ بياناتك من Google وMicrosoft لحظيًا فقط لتشغيل الميزات التي تطلبها، ولا تُنسخ.',
      detailLabel: 'ماذا يعني هذا عمليًا',
      groups: [
        {
          points: [
            {
              label: 'بياناتك لا تُستخدم لتدريب الذكاء الاصطناعي.',
              body: 'لا يُستخدم محتوى محادثاتك واجتماعاتك لتدريب نماذج الذكاء الاصطناعي، لا من جانبنا ولا من جانب مزودي خدماتنا. ونستخدم خدمات مدفوعة تضمن عدم تدريب نماذجها على محتوى العملاء.',
            },
            {
              label: 'لا تُباع ولا تُستخدم في الإعلانات.',
              body: 'لا نبيع بياناتك، ولا نستخدم محتوى محادثاتك واجتماعاتك للإعلانات أو لبناء ملفات تعريفية عنك.',
            },
            {
              label: 'تُقرأ لحظيًا ولا تُنسخ.',
              body: 'تُقرأ جهات اتصالك وتقويمك ومهامك وملفاتك لحظيًا فقط لتشغيل الميزات التي تطلبها، ولا تُخزن على خوادمنا.',
            },
            {
              label: 'تُرسل للتفريغ لا للتدريب.',
              body: 'للتفريغ والتلخيص، يُرسل محتوى اجتماعاتك إلى مزودي الذكاء الاصطناعي لدينا (OpenAI وGoogle Gemini) خارج المملكة، عبر باقات مدفوعة لا تُدرّب نماذجها عليه وتحذفه خلال نحو 30 يومًا. وما عدا ذلك من بياناتك يبقى داخل المملكة.',
            },
          ],
        },
      ],
      linkTo: '/privacy',
      linkLabel: 'اقرأ سياسة الخصوصية',
    },
    {
      id: 'prime',
      icon: 'lock',
      word: 'امتياز',
      subtitle: 'مشفرة، داخل المملكة، ومهيأة للتعافي من الكوارث.',
      summary:
        'اجتماعاتك مشفرة أثناء النقل وأثناء التخزين، ومخزنة داخل المملكة على Oracle Cloud Infrastructure، مع نسخ احتياطية تساعد على التعافي من الكوارث.',
      detailLabel: 'تفاصيل الأمان',
      groups: [
        {
          subhead: 'التشفير والاستضافة',
          points: [
            {
              label: 'مشفرة أثناء النقل.',
              body: 'كل اتصال يمر عبر HTTPS ببروتوكول TLS حديث (1.2 و 1.3). أما البروتوكولات الأقدم والأضعف فمعطلة، ويمكنك التأكد من إعداداتنا بأي أداة فحص SSL عامة.',
            },
            {
              label: 'مشفرة أثناء التخزين.',
              body: 'تُشفّر بياناتك أثناء التخزين بمعيار AES-256 عبر Oracle Cloud Infrastructure. وتُشفّر رموز الحسابات المرتبطة بطبقة إضافية من التشفير على مستوى التطبيق.',
            },
            {
              label: 'مخزنة في المملكة.',
              body: 'تبقى بيانات حسابك ومحادثاتك واجتماعاتك على بنية تحتية داخل المملكة: بياناتك الأساسية في الرياض، ونسخها الاحتياطية المشفرة في جدة.',
            },
            {
              label: 'بنية تحتية بمستوى المؤسسات.',
              body: 'يعمل برَينوتس على Oracle Cloud Infrastructure، الحاصلة على شهادتي ISO 27001 وSOC 2 لمراكز بياناتها.',
            },
          ],
        },
        {
          subhead: 'نسخ احتياطية مهيأة للتعافي من الكوارث',
          points: [
            {
              label: 'نسخ احتياطية يومية ومشفرة، ومفاتيحها محفوظة بمعزل عنها.',
              body: 'تُشفّر كل نسخة احتياطية قبل مغادرتها الخادم، ولا يُخزّن مفتاح فك تشفيرها أبدًا على الأجهزة التي تنشئها. لذلك، لا يعني الوصول إلى خوادمنا الوصول إلى محتوى النسخ الاحتياطية.',
            },
            {
              label: 'منطقة ثانية، وداخل المملكة أيضًا.',
              body: 'تُكتب النسخ الاحتياطية في جدة، وهي منطقة Oracle Cloud منفصلة تبعد نحو 850 كيلومترًا عن بياناتك الأساسية في الرياض، ولها بنيتها المستقلة من الطاقة والشبكة. يساعد هذا الفصل الجغرافي على عزل مخاطر مثل الحريق أو الفيضان أو الانقطاع الإقليمي في أحد الموقعين عن الآخر. وكلاهما داخل المملكة.',
            },
            {
              label: 'غير قابلة للتغيير، فلا يستطيع أحد إتلافها.',
              body: 'خلال أول 21 يومًا، لا يستطيع أحد تعديل النسخ الاحتياطية ولا حذفها: لا مهاجم، ولا موظفونا، ولا حتى نحن. وحتى الاختراق الكامل يبقي اجتماعاتك قابلة للاسترجاع.',
            },
          ],
        },
      ],
      linkTo: '/subprocessors',
      linkLabel: 'اطلع على من يعالج بياناتك',
    },
    {
      id: 'priority',
      icon: 'star',
      word: 'أولوية',
      subtitle: 'أنت أولًا، وزمام الأمر بيدك.',
      summary:
        'أنت من يقرر ما يحتفظ به برَينوتس: احذف أي اجتماع أو حسابك بالكامل متى شئت، وحقوقك محفوظة، وإن حدث خلل يومًا، نضع حمايتك أولًا.',
      detailLabel: 'كيف يبقى القرار بيدك',
      groups: [
        {
          points: [
            {
              label: 'زمام الأمر بيدك.',
              body: 'تُحذف تسجيلاتك الصوتية من أنظمتنا بعد انتهاء المعالجة. ولا يبقى إلا النص المفرغ والملاحظات حتى تحذفها. لا نحتفظ بالصوت إلا إذا فعّلت ميزة الاحتفاظ بالصوت، المتاحة في الباقات المدفوعة للمدة المشمولة في باقتك. وإذا ربطت Drive، تبقى نسختك في حسابك. وعند حذف اجتماع أو حسابك، يُزال من أنظمتنا التشغيلية فورًا، ويُحذف نهائيًا من نسخنا الاحتياطية المشفرة خلال 30 يومًا.',
            },
            {
              label: 'حقوقك محفوظة.',
              body: 'يمكنك الوصول إلى بياناتك أو تصحيحها أو حذفها متى شئت، ونستجيب لطلباتك بما يتوافق مع نظام حماية البيانات الشخصية السعودي.',
            },
            {
              label: 'وإن حدث خلل يومًا.',
              body: 'إذا وقع تسرب للبيانات قد يؤثر عليك، نُبلغ سدايا خلال 72 ساعة، ونُبلغك دون تأخير وبوضوح.',
            },
          ],
        },
      ],
      linkTo: '/privacy',
      linkLabel: 'اقرأ سياسة الخصوصية',
    },
  ],

  faqTitle: 'الأسئلة الشائعة',
  faq: [
    {
      q: 'كيف يُنشئ برَينوتس محاضر الاجتماعات؟',
      a: 'تسجّل اجتماعك مباشرة أو ترفع تسجيلا موجودا. يفرّغه برَينوتس إلى نص، ثم ينشئ ملخصا وأبرز القرارات والمهام، ويمكنك مراجعتها وتعديلها ومشاركتها.',
    },
    {
      q: 'هل برَينوتس آمن ومتوافق مع نظام حماية البيانات الشخصية السعودي؟',
      a: 'نعم. تشغّل برَينوتس شركة برييورا، المسجلة في السجل الوطني لحماية البيانات الشخصية لدى سدايا (رقم 3260007171)، والمبنية وفق نظام حماية البيانات الشخصية السعودي. تُخزّن بياناتك داخل المملكة، وتُشفّر أثناء النقل وأثناء التخزين.',
    },
    {
      q: 'هل أستطيع استخدام برَينوتس للاجتماعات السرية؟',
      a: 'نعم. يُشفّر محتوى الاجتماعات أثناء النقل وأثناء التخزين، وتُخزّن بياناتك داخل المملكة العربية السعودية، وتُدار وفق نظام حماية البيانات الشخصية السعودي. تسجيل الدخول بلا كلمة مرور، عبر حسابك في Google أو Microsoft، فلا توجد كلمة مرور خاصة ببرَينوتس يمكن سرقتها. وللتفريغ والتلخيص، يُعالج المحتوى لدى مزودي الذكاء الاصطناعي لدينا عبر باقات مدفوعة لا تتدرب على محتوى العملاء. وتبقى اجتماعاتك خاصة بك وبمن تختار مشاركتها معهم.',
    },
    {
      q: 'هل يرسل برَينوتس محادثاتي أو اجتماعاتي إلى أي جهات خارجية؟',
      a: 'إلى جهتين فقط: مزودي الذكاء الاصطناعي لدينا، للتفريغ والتلخيص (لا للتدريب أبدًا، ويُحذف المحتوى خلال نحو 30 يومًا)، وتخزينك السحابي، إذا ربطته. فإذا منحت برَينوتس صلاحية الوصول إلى Google Drive أو OneDrive، يحفظ تسجيل كل اجتماع ونصه المفرغ تلقائيًا في حسابك، لتبقى لديك نسخة تحت سيطرتك. ويمكنك رفض هذه الصلاحية أو إيقاف الحفظ التلقائي متى شئت. ولأن هذه الملفات في حسابك، فإن حذف الاجتماع من برَينوتس لا يحذفها من Drive أو OneDrive؛ بل تحذفها أنت من هناك.',
    },
    {
      q: 'هل تُستخدم محادثاتي أو اجتماعاتي أو بياناتي لتدريب نماذج الذكاء الاصطناعي؟',
      a: 'لا. لا تُستخدم بياناتك أبدًا لتدريب نماذج الذكاء الاصطناعي، لا نماذجنا ولا نماذج مزودينا. ونستخدم باقات ذكاء اصطناعي مدفوعة لا تتدرب على محتوى العملاء.',
    },
    {
      q: 'من يستطيع الوصول إلى اجتماعاتي وبياناتها؟',
      a: 'تبقى اجتماعاتك خاصة بك وبمن تختار مشاركتها معهم.',
    },
    {
      q: 'أين تُخزن بياناتي؟',
      a: 'في المملكة العربية السعودية: بياناتك الأساسية في الرياض، ونسخك الاحتياطية المشفرة في جدة، وكلاهما مستضاف على Oracle Cloud Infrastructure داخل المملكة.',
    },
  ],

  relatedTitle: 'اقرأ التفاصيل',
  related: [
    { to: '/privacy', label: 'سياسة الخصوصية' },
    { to: '/terms', label: 'شروط الخدمة' },
    { to: '/subprocessors', label: 'مزودو الخدمات' },
  ],
};
